import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import CloseIcon from '@mui/icons-material/Close';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LaunchOutlinedIcon from '@mui/icons-material/LaunchOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  convertDateToStringFormat,
  getPrivateAdminDashboardMissionPath,
  type FreelanceAmountParts,
  type FreelanceDaysComponent,
} from '@bloomays-lib/utils.shared';
import { ActivityDayDetails, GqlActivitySignature, Maybe } from '@bloomays-lib/types.shared';
import { FileViewer, HrefLink, Modal } from '@bloomays-lib/ui.shared';
import { StatusChip } from '@bloomays-lib/ui.shared/datagrid';
import {
  CraDashboardRow,
  FACT_CHIP,
  SIG_CHIP,
  SignatureState,
  craAmountBreakdown,
  craExpectedPurchaseLines,
  craRowVerificationDetail,
  craSignatureIpUrl,
  craSignatureTooltip,
  craStatusChip,
  formatCraDays,
} from '../../helpers/craDashboard';
import { CraMonthGrid, craDayAmountLabel, craDayAstreinteLabel } from '../organisms/missionDashboardV2/CraMonthGrid';

/** Onglets de la popin — l'historique de la cible n'est pas encore implémenté. */
export type CraVerificationTab = 'detail' | 'invoice';

export type CraVerificationModalProps = {
  /** Ligne CRA à vérifier — `undefined` ferme la popin. */
  row: CraDashboardRow | undefined;
  onClose: () => void;
  /** Ouvre l'aperçu du CRA signé (fichier sécurisé / page publique) via la visionneuse du tableau. */
  onPreviewCra?: (row: CraDashboardRow) => void;
  /** Ouvre une facture fournisseur en grand dans la visionneuse du tableau. */
  onPreviewInvoice?: (url: string, title: string) => void;
  /** Jeton d'accès aux fichiers sécurisés (facture fournisseur affichée dans l'onglet). */
  accessToken?: string;
};

const fmtMonth = (d?: Date | null) => (d ? convertDateToStringFormat(new Date(d), 'MM/yyyy') : '—');
const fmtDate = (d?: Date | null) => (d ? convertDateToStringFormat(new Date(d), 'dd/MM/yyyy') : '—');
/** Deux décimales au minimum pour aligner les totaux, la précision réelle du CRA au maximum. */
const fmtDays = (n: number) => formatCraDays(n, 2);

/**
 * Montant HT au centime : un contrôle de facture se joue sur les centimes, que le
 * `formatEuro` du tableau arrondit à l'euro pour rester compact.
 */
const fmtEuro = (amount: number | null | undefined): string =>
  amount === null || amount === undefined
    ? '—'
    : amount.toLocaleString('fr-FR', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

/** Libellé i18n d'une composante de jours du CRA. */
const DAYS_COMPONENT_KEY: Record<FreelanceDaysComponent, string> = {
  normal: 'daysComponentNormal',
  saturday: 'daysComponentSaturday',
  sundayHoliday: 'daysComponentSundayHoliday',
  astreinte: 'daysComponentAstreinte',
};

/** Pastille de signature préfixée (« Sig. Bloomer · Signé »), détails au survol. */
const SignatureChip = ({
  label,
  state,
  info,
}: {
  label: string;
  state: SignatureState;
  info: Maybe<GqlActivitySignature> | undefined;
}) => {
  const chip = <StatusChip tone={SIG_CHIP[state].tone}>{label}</StatusChip>;
  const tip = craSignatureTooltip(info);
  return tip ? (
    <Tooltip title={tip} arrow>
      <span>{chip}</span>
    </Tooltip>
  ) : (
    chip
  );
};

/**
 * Carte de signature : état, signataire, date et adresse IP relevée. L'IP tient sur sa
 * propre ligne et se coupe si besoin (une IPv6 fait jusqu'à 39 caractères) ; un clic
 * ouvre sa géolocalisation.
 */
const SignatureCardBlock = ({
  label,
  state,
  info,
  notSignedLabel,
  ipTitle,
}: {
  label: string;
  state: SignatureState;
  info: Maybe<GqlActivitySignature> | undefined;
  notSignedLabel: string;
  ipTitle: (ip: string) => string;
}) => {
  const signedAt = info?.signedAt ? convertDateToStringFormat(new Date(info.signedAt), 'dd/MM/yyyy à HH:mm') : '';
  const ip = info?.ip ?? '';
  const ipUrl = craSignatureIpUrl(ip);
  return (
    <SignatureCard>
      <SignatureHead>
        <SignatureLabel>{label}</SignatureLabel>
        <StatusChip tone={SIG_CHIP[state].tone}>{SIG_CHIP[state].label}</StatusChip>
      </SignatureHead>
      {info ? (
        <SignatureMeta>
          {info.signerName ? <Signer>{info.signerName}</Signer> : null}
          {signedAt ? <MetaLine>{signedAt}</MetaLine> : null}
          {ip ? (
            <IpRow>
              <PlaceOutlinedIcon sx={{ fontSize: 14 }} />
              {ipUrl ? (
                <HrefLink
                  href={ipUrl}
                  target="_blank"
                  color="tertiary"
                  title={ipTitle(ip)}
                  text={ip}
                  sx={{ fontSize: 11.5, wordBreak: 'break-all', display: 'inline' }}
                />
              ) : (
                <Ip>{ip}</Ip>
              )}
            </IpRow>
          ) : null}
        </SignatureMeta>
      ) : (
        <MetaLine>{notSignedLabel}</MetaLine>
      )}
    </SignatureCard>
  );
};

/** Tuile de total (jours ouvrés, samedi, astreinte, total CRA). */
const Total = ({ label, value, strong }: { label: string; value: number; strong?: boolean }) => (
  <TotalTile className={strong ? 'strong' : undefined}>
    <TotalLabel title={label}>{label}</TotalLabel>
    <TotalValue>{fmtDays(value)}</TotalValue>
  </TotalTile>
);

/** Ligne du tableau de montants (vente / achat / marge), décomposée normal / HNO. */
const AmountRow = ({
  label,
  parts,
  strong,
}: {
  label: string;
  parts: FreelanceAmountParts | null;
  strong?: boolean;
}) => (
  <tr className={strong ? 'strong' : undefined}>
    <th>{label}</th>
    <td>{parts ? fmtEuro(parts.normal) : '—'}</td>
    <td>{parts ? fmtEuro(parts.hno) : '—'}</td>
    <td>{parts ? fmtEuro(parts.total) : '—'}</td>
  </tr>
);

/**
 * Module de vérification d'un CRA depuis le tableau de bord des activités (TEC-3256).
 *
 * Deux lectures :
 * - « Détail du CRA » — rejoue le mois déclaré en lecture seule (grille calendaire
 *   `CraMonthGrid`, décomposition des jours, commentaires du Bloomer, signatures) ;
 * - « Vérification facture » — la facture fournisseur reçue face à tout l'attendu Bloomays
 *   (jours cumulés, TJM, calcul ligne à ligne, société du Bloomer, état de facturation).
 *
 * Les écarts ne sont pas encore calculés de façon structurée : la seule remontée disponible
 * est la dernière erreur de facturation fournisseur (`bloomerInvoiceLastError`). L'onglet
 * « Historique » de la cible n'est pas implémenté.
 */
const CraVerificationModal = ({
  row,
  onClose,
  onPreviewCra,
  onPreviewInvoice,
  accessToken,
}: CraVerificationModalProps) => {
  const { t } = useTranslation('craVerification');
  const detail = row ? craRowVerificationDetail(row) : null;
  const status = row ? craStatusChip(row) : undefined;
  const amounts = row ? craAmountBreakdown(row) : null;
  const expectedLines = row ? craExpectedPurchaseLines(row) : [];
  const [tab, setTab] = useState<CraVerificationTab>('detail');
  const [selectedDay, setSelectedDay] = useState<ActivityDayDetails | undefined>();
  const [invoiceIndex, setInvoiceIndex] = useState(0);

  // Marge estimée : disponible seulement si les deux TJM du mois sont connus.
  const margin: FreelanceAmountParts | null =
    amounts?.sales && amounts.purchase
      ? {
          normal: amounts.sales.normal - amounts.purchase.normal,
          hno: amounts.sales.hno - amounts.purchase.hno,
          total: amounts.sales.total - amounts.purchase.total,
        }
      : null;

  // Factures fournisseur reçues sur le mois (principale + additionnelle traitée à la main).
  const invoiceFiles = [
    { url: row?.invoiceURL, label: t('invoiceFileBloomer') },
    { url: row?.additionalInvoiceURL, label: t('invoiceFileAdditional') },
  ].filter((file): file is { url: string; label: string } => !!file.url);
  const invoiceFile = invoiceFiles[invoiceIndex] ?? invoiceFiles[0];

  // Un changement de CRA (ou la fermeture) repart d'une lecture vierge.
  useEffect(() => {
    setSelectedDay(undefined);
    setTab('detail');
    setInvoiceIndex(0);
  }, [row?.rowId]);

  return (
    <Modal
      open={!!row}
      onClose={onClose}
      noClose
      additionnalCSS={{
        width: 'min(1120px, 95vw)',
        padding: 0,
        maxHeight: '92vh',
        overflow: 'auto',
        border: 'none',
        borderRadius: '20px',
        // Le centrage hérité de la page rendrait l'en-tête et les cartes de signature illisibles.
        textAlign: 'left',
      }}
    >
      {!row ? null : (
        <div>
          {/* En-tête collant : l'identité du CRA reste visible pendant le contrôle des jours. */}
          <StickyHeader>
            <Head>
              <div>
                <Client>{row.client || '—'}</Client>
                <Subtitle>
                  {row.mission || '—'} · {row.bloomer || '—'}
                </Subtitle>
              </div>
              <IconButton aria-label={t('close')} size="small" onClick={onClose}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Head>

            <ChipRow>
              {status ? <StatusChip tone={status.tone}>{status.label}</StatusChip> : null}
              <StatusChip tone="neutral">{t('monthChip', { month: fmtMonth(row.activityMonth) })}</StatusChip>
              <SignatureChip
                label={t('bloomerSignatureChip', { state: SIG_CHIP[row.bloomerSignature].label })}
                state={row.bloomerSignature}
                info={row.bloomerSignatureInfo}
              />
              <SignatureChip
                label={t('clientSignatureChip', { state: SIG_CHIP[row.clientSignature].label })}
                state={row.clientSignature}
                info={row.clientSignatureInfo}
              />
              {row.activityId ? <Mono>{row.activityId}</Mono> : null}
            </ChipRow>

            <Tabs value={tab} onChange={(_, value: CraVerificationTab) => setTab(value)} aria-label={t('tabsLabel')}>
              <Tab value="detail" label={t('tabCraDetail')} />
              <Tab
                value="invoice"
                label={t('tabInvoiceCheck')}
                // La dernière erreur de facturation fournisseur est le seul signal d'écart
                // dont on dispose : elle se voit depuis l'onglet.
                icon={row.bloomerInvoiceLastError ? <ErrorOutlineIcon sx={{ fontSize: 16 }} /> : undefined}
                iconPosition="end"
              />
            </Tabs>
          </StickyHeader>

          {tab === 'detail' && detail ? (
            <Body>
              <MainColumn>
                <MonthTitle>{detail.monthLabel}</MonthTitle>
                <CraMonthGrid
                  days={detail.days}
                  variant="detailed"
                  selectedDayId={selectedDay?.id}
                  onSelectDay={setSelectedDay}
                />

                <Totals>
                  <Total label={t('totalWorkedDays')} value={detail.workedDays} />
                  <Total label={t('totalSaturdayDays')} value={detail.saturdayDays} />
                  <Total label={t('totalSundayHolidayDays')} value={detail.sundayHolidayDays} />
                  <Total label={t('totalAstreinteDays')} value={detail.astreinteDays} />
                  <Total label={t('totalCra')} value={detail.totalDays} strong />
                </Totals>

                <DayPanel>
                  <DayPanelHead>
                    <strong>
                      {selectedDay
                        ? t('selectedDay', { day: selectedDay.day, date: selectedDay.date })
                        : t('selectDayHint')}
                    </strong>
                    <DayCount>{t('commentedDays', { count: detail.commentedDaysCount })}</DayCount>
                  </DayPanelHead>
                  {selectedDay ? (
                    <DayPanelBody>
                      <DayAmount>{craDayAmountLabel(selectedDay) || t('zeroDay')}</DayAmount>
                      {craDayAstreinteLabel(selectedDay) ? (
                        <DayAstreinte>{craDayAstreinteLabel(selectedDay)}</DayAstreinte>
                      ) : null}
                      <DayComment>{selectedDay.comment || t('noComment')}</DayComment>
                    </DayPanelBody>
                  ) : null}
                </DayPanel>

                <Signatures>
                  <SectionTitle>{t('signaturesTitle')}</SectionTitle>
                  <SignatureCards>
                    <SignatureCardBlock
                      label={t('bloomerSignature')}
                      state={row.bloomerSignature}
                      info={row.bloomerSignatureInfo}
                      notSignedLabel={t('notSigned')}
                      ipTitle={(ip) => t('ipLinkTitle', { ip })}
                    />
                    <SignatureCardBlock
                      label={t('clientSignature')}
                      state={row.clientSignature}
                      info={row.clientSignatureInfo}
                      notSignedLabel={t('notSigned')}
                      ipTitle={(ip) => t('ipLinkTitle', { ip })}
                    />
                  </SignatureCards>
                </Signatures>
              </MainColumn>

              <SideColumn>
                <SectionTitle>{t('expectedInvoiceTitle')}</SectionTitle>
                <Panel>
                  <PanelRow>
                    <PanelKey>{t('expectedDays')}</PanelKey>
                    <PanelValue>{`${fmtDays(detail.totalDays)} j`}</PanelValue>
                  </PanelRow>
                  <PanelRow>
                    <PanelKey>{t('purchasePricePerDay')}</PanelKey>
                    <PanelValue>{fmtEuro(detail.purchasePricePerDay)}</PanelValue>
                  </PanelRow>
                  <PanelRow>
                    <PanelKey>{t('expectedAmountHT')}</PanelKey>
                    <PanelValue className="highlight">{fmtEuro(detail.expectedPurchaseAmountHT)}</PanelValue>
                  </PanelRow>
                </Panel>

                {/* Dernière erreur de facturation fournisseur remontée par l'API (lastSupplierError). */}
                {row.bloomerInvoiceLastError ? (
                  <Alert>
                    <ErrorOutlineIcon sx={{ fontSize: 18 }} />
                    <div>
                      <AlertTitle>{t('bloomerInvoiceErrorTitle')}</AlertTitle>
                      <AlertBody>{row.bloomerInvoiceLastError}</AlertBody>
                    </div>
                  </Alert>
                ) : null}

                <SectionTitle>{t('missionTitle')}</SectionTitle>
                <Panel>
                  <PanelRow>
                    <PanelKey>{t('missionClient')}</PanelKey>
                    <PanelValue>{row.client || '—'}</PanelValue>
                  </PanelRow>
                  <PanelRow>
                    <PanelKey>{t('missionLabel')}</PanelKey>
                    <PanelValue>{row.mission || '—'}</PanelValue>
                  </PanelRow>
                  <PanelRow>
                    <PanelKey>{t('bloomerSociety')}</PanelKey>
                    <PanelValue>{row.bloomerSocietyName || '—'}</PanelValue>
                  </PanelRow>
                  <PanelRow>
                    <PanelKey>{t('operationsContact')}</PanelKey>
                    <PanelValue>{row.contactOperationsName || '—'}</PanelValue>
                  </PanelRow>
                  <PanelRow>
                    <PanelKey>{t('salePricePerDay')}</PanelKey>
                    <PanelValue>{fmtEuro(row.salePricePerDayWithoutTaxes)}</PanelValue>
                  </PanelRow>
                  <PanelRow>
                    <PanelKey>{t('accountManager')}</PanelKey>
                    <PanelValue>{row.accountManager || '—'}</PanelValue>
                  </PanelRow>
                </Panel>

                <Links>
                  {row.craURL && onPreviewCra ? (
                    <LinkButton type="button" onClick={() => onPreviewCra(row)}>
                      {t('previewSignedCra')}
                      <LaunchOutlinedIcon sx={{ fontSize: 14 }} />
                    </LinkButton>
                  ) : null}
                  <HrefLink
                    href={getPrivateAdminDashboardMissionPath(row.missionId)}
                    target="_self"
                    color="tertiary"
                    icon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                    iconPosition="right"
                    text={t('openMission')}
                  />
                </Links>
              </SideColumn>
            </Body>
          ) : tab === 'invoice' ? (
            <InvoiceBody>
              {/* Facture reçue à gauche, tout l'attendu Bloomays à droite. */}
              <InvoicePane>
                <InvoiceBar>
                  <InvoiceName title={invoiceFile?.label}>{invoiceFile?.label ?? t('invoiceMissingTitle')}</InvoiceName>
                  {invoiceFiles.length > 1 ? (
                    <InvoiceSwitch>
                      {invoiceFiles.map((file, index) => (
                        <InvoiceSwitchButton
                          key={file.url}
                          type="button"
                          className={index === invoiceIndex ? 'active' : undefined}
                          onClick={() => setInvoiceIndex(index)}
                        >
                          {file.label}
                        </InvoiceSwitchButton>
                      ))}
                    </InvoiceSwitch>
                  ) : null}
                  {invoiceFile && onPreviewInvoice ? (
                    <LinkButton
                      type="button"
                      onClick={() => onPreviewInvoice(invoiceFile.url, `${invoiceFile.label} — ${row.bloomer}`)}
                    >
                      {t('openInvoice')}
                      <LaunchOutlinedIcon sx={{ fontSize: 14 }} />
                    </LinkButton>
                  ) : null}
                </InvoiceBar>
                {invoiceFile ? (
                  <InvoiceFrame>
                    <FileViewer url={invoiceFile.url} accessToken={accessToken} height="100%" />
                  </InvoiceFrame>
                ) : (
                  <InvoiceEmpty>{t('invoiceMissing')}</InvoiceEmpty>
                )}
              </InvoicePane>

              <SideColumn>
                {/* Dernière erreur de facturation fournisseur remontée par l'API (lastSupplierError). */}
                {row.bloomerInvoiceLastError ? (
                  <Alert>
                    <ErrorOutlineIcon sx={{ fontSize: 18 }} />
                    <div>
                      <AlertTitle>{t('bloomerInvoiceErrorTitle')}</AlertTitle>
                      <AlertBody>{row.bloomerInvoiceLastError}</AlertBody>
                    </div>
                  </Alert>
                ) : null}
                {/* Les écarts ne sont pas encore calculés : on affiche l'attendu, le contrôle reste humain. */}
                <Notice>{t('gapsNotComputedNotice')}</Notice>

                <SectionTitle>{t('expectedInvoiceTitle')}</SectionTitle>
                <Panel>
                  <PanelRow>
                    <PanelKey>{t('expectedDays')}</PanelKey>
                    <PanelValue>{`${fmtDays(row.daysEntered)} j`}</PanelValue>
                  </PanelRow>
                  <PanelRow>
                    <PanelKey>{t('purchasePricePerDay')}</PanelKey>
                    <PanelValue>{fmtEuro(row.purchasePricePerDayWithoutTaxes)}</PanelValue>
                  </PanelRow>
                  <PanelRow>
                    <PanelKey>{t('expectedAmountHT')}</PanelKey>
                    <PanelValue className="highlight">{fmtEuro(amounts?.purchase?.total)}</PanelValue>
                  </PanelRow>
                </Panel>

                <SectionTitle>{t('expectedCalculationTitle')}</SectionTitle>
                <Panel>
                  {expectedLines.length ? (
                    <>
                      {expectedLines.map((line) => (
                        <PanelRow key={line.component}>
                          <PanelKey>{t(DAYS_COMPONENT_KEY[line.component])}</PanelKey>
                          <PanelValue>
                            {line.coefficient === 1
                              ? t('expectedLine', {
                                  days: formatCraDays(line.days),
                                  price: fmtEuro(row.purchasePricePerDayWithoutTaxes),
                                  amount: fmtEuro(line.amount),
                                })
                              : t('expectedLineWithCoefficient', {
                                  days: formatCraDays(line.days),
                                  price: fmtEuro(row.purchasePricePerDayWithoutTaxes),
                                  coefficient: formatCraDays(line.coefficient),
                                  amount: fmtEuro(line.amount),
                                })}
                          </PanelValue>
                        </PanelRow>
                      ))}
                      <PanelRow>
                        <PanelKey>{t('expectedTotal')}</PanelKey>
                        <PanelValue className="highlight">{fmtEuro(amounts?.purchase?.total)}</PanelValue>
                      </PanelRow>
                    </>
                  ) : (
                    <MutedLine>{t('purchasePriceUnknown')}</MutedLine>
                  )}
                </Panel>

                <SectionTitle>{t('amountsTitle')}</SectionTitle>
                <TableScroller>
                  <AmountTable>
                    <thead>
                      <tr>
                        <th />
                        <th>{t('amountsNormal')}</th>
                        <th>{t('amountsHno')}</th>
                        <th>{t('amountsTotal')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <AmountRow label={t('amountsSales')} parts={amounts?.sales ?? null} />
                      <AmountRow label={t('amountsPurchase')} parts={amounts?.purchase ?? null} />
                      <AmountRow label={t('amountsMargin')} parts={margin} strong />
                    </tbody>
                  </AmountTable>
                </TableScroller>

                <SectionTitle>{t('craSummaryTitle')}</SectionTitle>
                <Panel>
                  <PanelRow>
                    <PanelKey>{t('monthOfService')}</PanelKey>
                    <PanelValue>
                      {detail ? `${detail.monthLabel} · ${fmtMonth(row.activityMonth)}` : fmtMonth(row.activityMonth)}
                    </PanelValue>
                  </PanelRow>
                  <PanelRow>
                    <PanelKey>{t('daysBreakdown')}</PanelKey>
                    <PanelValue>
                      {t('daysBreakdownValue', {
                        worked: formatCraDays(row.nbDaysWorked ?? 0),
                        saturday: formatCraDays(row.nbDaysMarkupWorked ?? 0),
                        sundayHoliday: formatCraDays(row.nbDaysMarkup2Worked ?? 0),
                        astreinte: formatCraDays(row.nbDaysAstreinteWorked ?? 0),
                      })}
                    </PanelValue>
                  </PanelRow>
                  {detail && detail.partialDaysCount > 0 ? (
                    <PanelRow>
                      <PanelKey>{t('partialDays')}</PanelKey>
                      <PanelValue>{t('partialDaysValue', { count: detail.partialDaysCount })}</PanelValue>
                    </PanelRow>
                  ) : null}
                  <PanelRow>
                    <PanelKey>{t('hoursPerDay')}</PanelKey>
                    <PanelValue>{row.hoursPerDays ?? '—'}</PanelValue>
                  </PanelRow>
                  <PanelRow>
                    <PanelKey>{t('craStatusLabel')}</PanelKey>
                    <PanelValue>{status ? <StatusChip tone={status.tone}>{status.label}</StatusChip> : '—'}</PanelValue>
                  </PanelRow>
                  {detail && detail.commentedDaysCount > 0 ? (
                    <PanelRow>
                      <PanelKey>{t('commentedDaysLabel')}</PanelKey>
                      <PanelValue>{t('commentedDays', { count: detail.commentedDaysCount })}</PanelValue>
                    </PanelRow>
                  ) : null}
                </Panel>

                <SectionTitle>{t('societyTitle')}</SectionTitle>
                <Panel>
                  <PanelRow>
                    <PanelKey>{t('societyName')}</PanelKey>
                    <PanelValue>{row.bloomerSocietyName || '—'}</PanelValue>
                  </PanelRow>
                  <PanelRow>
                    <PanelKey>{t('societyPortage')}</PanelKey>
                    <PanelValue>{row.bloomerSocietyPortage ? t('yes') : t('no')}</PanelValue>
                  </PanelRow>
                </Panel>

                <SectionTitle>{t('supplierInvoiceTitle')}</SectionTitle>
                <Panel>
                  <PanelRow>
                    <PanelKey>{t('bloomerInvoiceState')}</PanelKey>
                    <PanelValue>
                      <StatusChip tone={FACT_CHIP[row.bloomerFact].tone}>{FACT_CHIP[row.bloomerFact].label}</StatusChip>
                    </PanelValue>
                  </PanelRow>
                  <PanelRow>
                    <PanelKey>{t('invoiceReceivedAt')}</PanelKey>
                    <PanelValue>{fmtDate(row.dateUploadInvoice)}</PanelValue>
                  </PanelRow>
                  <PanelRow>
                    <PanelKey>{t('supplierInvoiceState')}</PanelKey>
                    <PanelValue>{row.supplierInvoiceStatus ?? '—'}</PanelValue>
                  </PanelRow>
                  <PanelRow>
                    <PanelKey>{t('externalSupplierInvoiceId')}</PanelKey>
                    <PanelValue>{row.externalSupplierInvoiceId ?? '—'}</PanelValue>
                  </PanelRow>
                  <PanelRow>
                    <PanelKey>{t('payDelay')}</PanelKey>
                    <PanelValue>
                      {row.bloomerPayDateDays === null || row.bloomerPayDateDays === undefined
                        ? '—'
                        : t('payDelayValue', { days: row.bloomerPayDateDays })}
                    </PanelValue>
                  </PanelRow>
                </Panel>

                {row.bloomerInvoicePennylaneUrl ? (
                  <Links>
                    <HrefLink
                      href={row.bloomerInvoicePennylaneUrl}
                      target="_blank"
                      color="tertiary"
                      icon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                      iconPosition="right"
                      text={t('openInPennylane')}
                    />
                  </Links>
                ) : null}
              </SideColumn>
            </InvoiceBody>
          ) : (
            <Empty>{t('unavailable')}</Empty>
          )}
        </div>
      )}
    </Modal>
  );
};

export default CraVerificationModal;
export { CraVerificationModal };

// ── Styled ───────────────────────────────────────────────────────────────────

const StickyHeader = styled('div')(({ theme }) => ({
  position: 'sticky',
  top: 0,
  zIndex: 1,
  backgroundColor: theme.palette.neutral.white,
  borderBottom: `1px solid ${theme.palette.neutral[15]}`,
}));

const Head = styled('div')(() => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 12,
  padding: '20px 24px 8px',
}));

const Client = styled('div')(({ theme }) => ({
  fontSize: 18,
  fontWeight: 800,
  textTransform: 'uppercase',
  color: theme.palette.neutral[90],
}));

const Subtitle = styled('div')(({ theme }) => ({
  fontSize: 12.5,
  color: theme.palette.neutral[50],
  marginTop: 2,
}));

const ChipRow = styled('div')(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 8,
  padding: '0 24px 12px',
}));

const Mono = styled('span')(({ theme }) => ({
  fontFamily: 'monospace',
  fontSize: 11.5,
  padding: '3px 8px',
  borderRadius: 999,
  backgroundColor: theme.palette.neutral[10],
  color: theme.palette.neutral[70],
}));

/**
 * Deux colonnes : le mois déclaré à gauche, ce qui est attendu sur la facture et la mission à
 * droite (empilées sous 900px). La colonne de gauche est bornée pour garder des cases carrées
 * lisibles dans le calendrier.
 */
const Body = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 520px) minmax(0, 1fr)',
  gap: 24,
  padding: '20px 24px 24px',
  [theme.breakpoints.down(900)]: { gridTemplateColumns: 'minmax(0, 1fr)' },
}));

/**
 * Onglet facture : la pièce reçue prend la place principale, l'attendu Bloomays tient
 * dans une colonne de lecture à droite (empilées sous 900px).
 */
const InvoiceBody = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 400px)',
  gap: 24,
  padding: '20px 24px 24px',
  [theme.breakpoints.down(900)]: { gridTemplateColumns: 'minmax(0, 1fr)' },
}));

const InvoicePane = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  minWidth: 0,
}));

const InvoiceBar = styled('div')(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 12,
}));

const InvoiceName = styled('div')(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  fontSize: 13,
  fontWeight: 700,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  color: theme.palette.neutral[90],
}));

const InvoiceSwitch = styled('div')(({ theme }) => ({
  display: 'inline-flex',
  gap: 2,
  padding: 2,
  borderRadius: 999,
  backgroundColor: theme.palette.neutral[10],
}));

const InvoiceSwitchButton = styled('button')(({ theme }) => ({
  border: 'none',
  borderRadius: 999,
  padding: '4px 10px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 11.5,
  fontWeight: 600,
  background: 'transparent',
  color: theme.palette.neutral[60],
  '&.active': {
    backgroundColor: theme.palette.neutral.white,
    color: theme.palette.neutral[90],
  },
}));

/** Cadre de la visionneuse : hauteur fixe pour que la facture se lise sans zoomer la popin. */
const InvoiceFrame = styled('div')(({ theme }) => ({
  height: 'min(62vh, 620px)',
  border: `1px solid ${theme.palette.neutral[15]}`,
  borderRadius: 10,
  overflow: 'hidden',
}));

const InvoiceEmpty = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: 'min(40vh, 320px)',
  border: `1px dashed ${theme.palette.neutral[15]}`,
  borderRadius: 10,
  fontSize: 13,
  color: theme.palette.neutral[60],
  textAlign: 'center',
  padding: 16,
}));

const Notice = styled('div')(({ theme }) => ({
  border: `1px solid ${theme.palette.neutral[15]}`,
  backgroundColor: theme.palette.neutral[10],
  borderRadius: 10,
  padding: '8px 12px',
  fontSize: 12,
  color: theme.palette.neutral[60],
}));

const MutedLine = styled('div')(({ theme }) => ({
  padding: '9px 0',
  fontSize: 12.5,
  fontStyle: 'italic',
  color: theme.palette.neutral[60],
}));

/** Le tableau de montants défile dans sa propre boîte plutôt que d'élargir la colonne. */
const TableScroller = styled('div')(() => ({
  overflowX: 'auto',
  minWidth: 0,
}));

/** Montants vente / achat / marge décomposés normal / HNO, comme le drawer de détail. */
const AmountTable = styled('table')(({ theme }) => ({
  width: '100%',
  borderCollapse: 'collapse',
  border: `1px solid ${theme.palette.neutral[15]}`,
  borderRadius: 10,
  fontSize: 12,
  'th, td': { padding: '6px 8px', textAlign: 'right', whiteSpace: 'nowrap' },
  'thead th': {
    fontSize: 9.5,
    fontWeight: 700,
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
    color: theme.palette.neutral[50],
  },
  'tbody th': { textAlign: 'left', fontWeight: 600, color: theme.palette.neutral[60] },
  'tbody td': { fontVariantNumeric: 'tabular-nums', color: theme.palette.neutral[90] },
  'tbody tr:not(:last-of-type) th, tbody tr:not(:last-of-type) td': {
    borderBottom: `1px solid ${theme.palette.neutral[10]}`,
  },
  'tbody tr.strong th, tbody tr.strong td': { fontWeight: 800, color: theme.palette.neutral[90] },
}));

const MainColumn = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  minWidth: 0,
}));

const SideColumn = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  minWidth: 0,
}));

const Panel = styled('div')(({ theme }) => ({
  border: `1px solid ${theme.palette.neutral[15]}`,
  borderRadius: 10,
  padding: '4px 14px',
}));

const PanelRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: 12,
  padding: '9px 0',
  '&:not(:last-of-type)': { borderBottom: `1px solid ${theme.palette.neutral[10]}` },
}));

const PanelKey = styled('span')(({ theme }) => ({
  fontSize: 12.5,
  color: theme.palette.neutral[60],
}));

const PanelValue = styled('span')(({ theme }) => ({
  fontSize: 12.5,
  fontWeight: 700,
  textAlign: 'right',
  color: theme.palette.neutral[90],
  '&.highlight': { fontSize: 15, color: theme.palette.blue[60] },
}));

const Alert = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 8,
  border: `1px solid ${theme.palette.red[40]}`,
  backgroundColor: theme.palette.red[20],
  borderRadius: 10,
  padding: '10px 12px',
  color: theme.palette.red[80],
}));

const AlertTitle = styled('div')(() => ({
  fontSize: 12.5,
  fontWeight: 700,
}));

const AlertBody = styled('div')(() => ({
  fontSize: 12,
  overflowWrap: 'anywhere',
}));

const Links = styled('div')(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 16,
  paddingTop: 4,
}));

const LinkButton = styled('button')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  border: 'none',
  background: 'none',
  padding: 0,
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 12,
  fontWeight: 600,
  color: theme.palette.blue[60],
  '&:hover': { opacity: 0.8 },
}));

const MonthTitle = styled('div')(({ theme }) => ({
  fontSize: 15,
  fontWeight: 700,
  color: theme.palette.neutral[90],
}));

/**
 * Décomposition des jours : une seule ligne de tuiles, calée sur la largeur de la grille du
 * mois juste au-dessus. Les colonnes sont fixes (`minmax(0, 1fr)`) — en `auto-fit` la dernière
 * tuile passait à la ligne dès que la colonne se resserrait, cassant la lecture du récapitulatif.
 */
const Totals = styled('div')(() => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
  gap: 6,
}));

const TotalTile = styled('div')(({ theme }) => ({
  border: `1px solid ${theme.palette.neutral[15]}`,
  borderRadius: 10,
  padding: '7px 8px',
  minWidth: 0,
  overflow: 'hidden',
  '&.strong': {
    borderColor: theme.palette.blue[60],
    backgroundColor: theme.palette.blue[20],
  },
}));

/** Libellé tenant sur une ligne ; tronqué (avec info-bulle) plutôt que d'élargir la tuile. */
const TotalLabel = styled('div')(({ theme }) => ({
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: '0.02em',
  textTransform: 'uppercase',
  color: theme.palette.neutral[50],
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

const TotalValue = styled('div')(({ theme }) => ({
  fontSize: 16,
  fontWeight: 800,
  fontVariantNumeric: 'tabular-nums',
  color: theme.palette.neutral[90],
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

const DayPanel = styled('div')(({ theme }) => ({
  border: `1px solid ${theme.palette.neutral[15]}`,
  borderRadius: 10,
  padding: '10px 14px',
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
}));

const DayPanelHead = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: 12,
  fontSize: 13,
  color: theme.palette.neutral[90],
}));

const DayCount = styled('span')(({ theme }) => ({
  fontSize: 11.5,
  color: theme.palette.neutral[50],
  whiteSpace: 'nowrap',
}));

const DayPanelBody = styled('div')(() => ({
  display: 'flex',
  alignItems: 'baseline',
  gap: 10,
}));

const DayAmount = styled('span')(({ theme }) => ({
  fontSize: 12,
  fontWeight: 700,
  padding: '2px 8px',
  borderRadius: 999,
  backgroundColor: theme.palette.neutral[10],
  color: theme.palette.neutral[70],
  whiteSpace: 'nowrap',
}));

const DayAstreinte = styled('span')(({ theme }) => ({
  fontSize: 12,
  fontWeight: 700,
  padding: '2px 8px',
  borderRadius: 999,
  backgroundColor: theme.palette.red[20],
  color: theme.palette.red[80],
  whiteSpace: 'nowrap',
}));

const DayComment = styled('span')(({ theme }) => ({
  fontSize: 12.5,
  color: theme.palette.neutral[60],
}));

const Signatures = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  borderTop: `1px solid ${theme.palette.neutral[15]}`,
  paddingTop: 14,
}));

const SectionTitle = styled('div')(({ theme }) => ({
  fontSize: 9.5,
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: theme.palette.neutral[50],
}));

const SignatureCards = styled('div')(() => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
  gap: 10,
}));

const SignatureCard = styled('div')(({ theme }) => ({
  border: `1px solid ${theme.palette.neutral[15]}`,
  borderRadius: 10,
  padding: '10px 12px',
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  minWidth: 0,
}));

const SignatureHead = styled('div')(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
}));

const SignatureLabel = styled('span')(({ theme }) => ({
  fontSize: 12.5,
  fontWeight: 700,
  color: theme.palette.neutral[90],
}));

const SignatureMeta = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  minWidth: 0,
}));

const Signer = styled('span')(({ theme }) => ({
  fontSize: 12.5,
  fontWeight: 600,
  color: theme.palette.neutral[70],
}));

const MetaLine = styled('span')(({ theme }) => ({
  fontSize: 11.5,
  color: theme.palette.neutral[60],
}));

const IpRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 4,
  minWidth: 0,
  color: theme.palette.neutral[50],
}));

const Ip = styled('span')(({ theme }) => ({
  fontSize: 11.5,
  wordBreak: 'break-all',
  color: theme.palette.neutral[60],
}));

const Empty = styled('div')(({ theme }) => ({
  padding: 24,
  fontSize: 13,
  color: theme.palette.neutral[60],
}));
